import React, { useState } from 'react';
import { 
  Check, 
  Lightbulb, 
  Users, 
  Star,
  ThumbsUp,
  UserCheck,
  GraduationCap
} from 'lucide-react';
import KickstartBanner from '../../components/KickstartBanner/KickstartBanner';
import './Home.css';
import HeroSection from '../../components/Hero/Hero';

function Home({ onSelectCourse, onNavigateToContact }) {
  const [activeCourseTab, setActiveCourseTab] = useState('All Programs');
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);

  const whyChooseCards = [
    {
      id: 0,
      title: "Personalised Guidance",
      desc: "Tailored guidance for every student’s strengths, interests, and goals.",
      image: "/images/Home/Why1.jpeg",
      iconDark: <ThumbsUp size={28} strokeWidth={2} />
    },
    {
      id: 1,
      title: "Clarity Before Choice",
      desc: "We help students understand themselves before deciding what they should become.",
      image: "/images/Home/Why2.jpeg",
      iconDark: <UserCheck size={28} strokeWidth={2} />
    },
    {
      id: 2,
      title: "Future-Ready Thinking",
      desc: "Explore future careers and opportunities with greater confidence and clarity.",
      image: "/images/Home/Why3.png",
      iconDark: <GraduationCap size={28} strokeWidth={2} />
    }
  ];

  const courseCategories = ['All Programs', 'School Students', 'Higher Education', 'Working Professionals'];

  const coursesData = [
    {
      id: 1,
      title: "Beyond Marks",
      target: "For Class 7–8",
      category: "School Students",
      desc: "Discover strengths, interests and possibilities beyond academic marks.",
      level: "School",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course1.jpeg"
    },
    {
      id: 2,
      title: "Future Pathways",
      target: "For Class 9–10",
      category: "School Students",
      desc: "Future Pathways helps Class 9–10 students understand their strengths, interests and aspirations, explore suitable career directions, and build clarity before making important stream and subject choices.",
      level: "School",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course2.jpeg"
    },
    {
      id: 3,
      slug: "global-pathways",
      title: "Global Pathways",
      target: "For Class 9–11",
      category: "School Students",
      desc: "Global Pathways helps students explore undergraduate study options abroad with greater clarity. It connects self-discovery, career direction and future goals with the right higher education pathways, helping students make informed decisions about countries, courses and universities.",
      level: "School",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course3.jpeg"
    },
    {
      id: 4,
      slug: "career-discovery",
      title: "Career Discovery",
      target: "For Class 11–12",
      category: "School Students",
      desc: "Career Discovery helps Class 11–12 students understand themselves, explore right-fit career pathways and make confident decisions about their future. Through structured self-discovery, scientific assessment and guided career research, students build clarity before choosing their next step.",
      level: "School",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course3.jpeg"
    },
    {
      id: 5,
      slug: "career-launch",
      title: "Career Launch",
      target: "For Undergraduate Students",
      category: "Higher Education",
      desc: "Career Launch helps undergraduate students understand their strengths, interests and career goals, explore suitable career pathways and prepare for a confident transition from college to the world of work.",
      level: "College",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course4.jpeg"
    },
    {
      id: 6,
      slug: "global-pathways-postgrad",
      title: "Global Pathways",
      target: "For Master’s Abroad",
      category: "Higher Education",
      desc: "Global Pathways helps undergraduate students understand their strengths and career goals, explore suitable Master’s pathways abroad, and make informed decisions about higher education and their future.",
      level: "College",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course6.jpeg"
    },
    {
      id: 7,
      slug: "career-acceleration",
      title: "Career Acceleration",
      target: "For Postgraduate Students",
      category: "Higher Education",
      desc: "Career Acceleration helps postgraduate students gain clarity on their career direction, understand their strengths and interests, and make informed decisions about their next professional step.",
      level: "College",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course5.jpeg"
    },
    {
      id: 8,
      slug: "career-transition",
      title: "Career Transition",
      target: "For Young Working Professionals",
      category: "Working Professionals",
      desc: "Career Transition helps young working professionals gain clarity about their strengths, interests and career goals while exploring suitable directions for their next professional chapter.",
      level: "Professional",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course6.jpeg"
    },
    {
      id: 9,
      slug: "career-revival",
      title: "Career Revival",
      target: "For Unemployed Graduates",
      category: "Working Professionals",
      desc: "Career Revival helps unemployed graduates gain clarity about their strengths, interests and career direction while exploring suitable pathways for their next professional opportunity.",
      level: "Professional",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course2.jpeg"
    },
    {
      id: 10,
      slug: "career-restart",
      title: "Career Restart",
      target: "For Unemployed Non-Graduates",
      category: "Working Professionals",
      desc: "Career Restart helps unemployed non-graduates understand their strengths, interests and possibilities while exploring practical career directions and building a clear path toward their next opportunity.",
      level: "Professional",
      price: "$299.9",
      rating: "4.9",
      image: "images/Home/Course1.jpeg"
    }
  ];

  const testimonials = [
    {
      id: 0,
      name: "Parent",
      role: "Parent of Grade 12 Student",
      avatar: "/images/avatar1.jpg",
      quote: "My son finally felt confident about his career direction after months of confusion. The sessions brought immense clarity."
    },
    {
      id: 1,
      name: "Undergraduate Student",
      role: "Undergraduate Student",
      avatar: "/images/avatar2.jpg",
      quote: "The guidance felt personal and practical. I understood my strengths for the first time."
    },
    {
      id: 2,
      name: "Parent",
      role: "Parent",
      avatar: "/images/avatar3.jpg",
      quote: "DC helped us as parents understand how to support our child without pressure."
    }
  ];

  const filteredCourses = activeCourseTab === 'All Programs' 
    ? coursesData 
    : coursesData.filter(course => course.category === activeCourseTab);

  const currentTestimonial = testimonials[activeTestimonialIndex];

  return (
    <div className="home-page">
      {/* ================= HERO SECTION (CONSULOX HOME V2 STYLE) ================= */}
    <HeroSection/>

      {/* ================= ABOUT SECTION ================= */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-left-col reveal-left">
              <div className="about-header">
                <div className="about-badge"> ABOUT DEEPCLARITI</div>
                <h2 className="about-main-title"> 
                  More Than Career Counseling. A Human Approach to Guidance.
                </h2>
                <p className="about-intro-text">
                  Deepclariti™ helps students discover their strengths, interests, personality, aspirations and possibilities before making important education and career decisions. Through career coaching, educational planning, parent guidance and future-readiness mentoring, we help students move from confusion to clarity and make decisions they can genuinely own.
                </p>
              </div>

              <div className="about-sub-grid">
                <div className="executive-img-box">
                  <img src="/images/Home/About2.jpeg" alt="Professional Executive Coach" />
                </div>

                <div className="stats-stack">
                  <div className="stat-card">
                    <div className="stat-icon-wrapper"><Lightbulb size={24} /></div>
                    <div className="stat-number">25+</div>
                    <div className="stat-label">Years in Higher Education</div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon-wrapper"><Users size={24} /></div>
                    <div className="stat-number">10,000+</div>
                    <div className="stat-label">Students Personally Coached</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="about-right-col reveal-right">
             <div className="about-img-box">
                <img 
                  src="/images/Home/About1.jpeg" 
                  alt="Coaching client working happily with headphones" 
                />
              </div>

              <div className="features-list-container">
                <ul className="features-list">
                  <li className="feature-item">
                    <div className="feature-check-icon"><Check size={14} strokeWidth={3} /></div>
                    <div className="feature-text"><strong>Career Mentoring</strong> — Personalised guidance focused on confidence, decision-making and long-term growth.</div>
                  </li>
                  <li className="feature-item">
                    <div className="feature-check-icon"><Check size={14} strokeWidth={3} /></div>
                    <div className="feature-text"><strong>Educational Planning</strong> — Clarity around streams, courses, degrees, specialisations and higher-study pathways.</div>
                  </li>
                  <li className="feature-item">
                    <div className="feature-check-icon"><Check size={14} strokeWidth={3} /></div>
                    <div className="feature-text"><strong>Self-Discovery</strong> — Understand strengths, interests, personality, motivations and aspirations.</div>
                  </li>
                  <li className="feature-item">
                    <div className="feature-check-icon"><Check size={14} strokeWidth={3} /></div>
                    <div className="feature-text"><strong>Parent Guidance</strong> — Helping parents support their children with understanding and confidence, without unnecessary pressure.</div>
                  </li>
                </ul>

                <div className="about-quote-box">
                  <p>"Every student deserves guidance that understands who they are — not just what society expects them to become."</p>
                </div>

                <button onClick={onNavigateToContact} className="btn-about-more">
                  <span>Discover Our Approach</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 6-CARD FEATURE GRID SECTION ================= */}
      <section className="feature-grid-section">
        <div className="container">
          <div className="feature-grid-header reveal-on-scroll">
            <h2 className="feature-grid-title">Clarity Starts With Understanding Yourself</h2>
            <p className="feature-grid-subtitle">
              Before choosing a course, college or career, we help students understand their strengths, interests, personality and aspirations — then turn that understanding into confident decisions.
            </p>
          </div>

          <div className="feature-grid-container reveal-stagger">
            {/* Card 01 — Image */}
            <div className="feature-image-card">
              <img src="/images/Home/card1.jpeg" alt="Student engaging in self discovery" />
            </div>

            {/* Card 02 — Career Mentoring */}
            <div className="feature-card">
              <div className="feature-card-icon"><UserCheck size={26} strokeWidth={1.8} /></div>
              <h3 className="feature-card-title">Career Mentoring</h3>
              <p className="feature-card-desc">Personalised guidance that helps students build clarity, confidence and ownership of their career decisions.</p>
            </div>

            {/* Card 03 — Educational Planning */}
            <div className="feature-card">
              <div className="feature-card-icon"><GraduationCap size={26} strokeWidth={1.8} /></div>
              <h3 className="feature-card-title">Educational Planning</h3>
              <p className="feature-card-desc">Navigate streams, courses, degrees, specialisations and higher-education pathways with greater clarity.</p>
            </div>

            {/* Card 04 — Skill & Passion Discovery */}
            <div className="feature-card">
              <div className="feature-card-icon"><Lightbulb size={26} strokeWidth={1.8} /></div>
              <h3 className="feature-card-title">Skill &amp; Passion Discovery</h3>
              <p className="feature-card-desc">Understand strengths, interests, personality, motivations and aspirations before choosing a career direction.</p>
            </div>

            {/* Card 05 — Parent Guidance */}
            <div className="feature-card">
              <div className="feature-card-icon"><Users size={26} strokeWidth={1.8} /></div>
              <h3 className="feature-card-title">Parent Guidance</h3>
              <p className="feature-card-desc">Help parents support their children with understanding, confidence and healthier conversations around education and careers.</p>
            </div>

            {/* Card 06 — Image */}
            <div className="feature-image-card">
              <img src="/images/Home/card2.jpeg" alt="Advisor mentoring student" />
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE DEEPCLARITI SECTION ================= */}
      <section id="why-us" className="why-choose-section">
        <div className="container">
          <div className="why-choose-header reveal-on-scroll">
            <h2 className="why-choose-title">Why Choose Deepclariti</h2>
            <p className="why-choose-subtitle">
              Career decisions become clearer when students understand themselves first. Our approach combines personalised coaching, self-discovery, scientific assessment and future-focused guidance.
            </p>
          </div>

          <div className="why-choose-grid reveal-stagger">
            {whyChooseCards.map((card) => (
              <div key={card.id} className="why-card is-active">
                <div className="why-card-bg" style={{ backgroundImage: `url(${card.image})` }} />
                <div className="why-card-overlay" />

                <div className="why-card-hover-box">
                  <div className="overlay-icon-wrapper">{card.iconDark}</div>
                  <h3 className="overlay-title">{card.title}</h3>
                  <p className="overlay-desc">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= OUR NEW COURSES SECTION ================= */}
      <section id="courses" className="courses-section">
        <div className="container">
          <div className="courses-header reveal-on-scroll">
            <h2 className="courses-title">
              Career Guidance for Every Stage
            </h2>
            <p className="courses-subtitle">
              From school and higher education to career transitions, Deepclariti™ helps students and professionals make informed decisions with greater clarity and confidence.
            </p>
          </div>

          <div className="courses-tabs reveal-on-scroll">
            {courseCategories.map((cat, idx) => (
              <button
                key={idx}
                className={`course-tab-btn ${activeCourseTab === cat ? 'active' : ''}`}
                onClick={() => setActiveCourseTab(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="courses-grid reveal-stagger">
            {filteredCourses.map((course) => (
              <div key={course.id} className="course-card">
                <div className="course-img-wrapper">
                  <img src={course.image} alt={course.title} className="course-img" />
                </div>

                <div className="course-meta-top">
                  <div className="rating-box">
                    <div className="stars-group">
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                    </div>
                    <span className="rating-score">({course.rating})</span>
                  </div>
                </div>

                <h3 className="course-card-title">{course.title}</h3>
                <p className="course-target-label">{course.target}</p>
                <p className="course-card-desc">{course.desc}</p>

                <button 
                  onClick={() => onSelectCourse(course)} 
                  className="btn-view-course"
                >
                  View Program 
                </button>
              </div>
            ))}
          </div>

          <div className="courses-footer-cta reveal-on-scroll">
            <button onClick={() => setActiveCourseTab('All Programs')} className="btn-all-courses">
              <span>View All Programs</span>
            </button>
          </div>
        </div>
      </section>

      {/* ================= FOUNDER, TESTIMONIAL & KICKSTART SECTION ================= */}
      <section id="founder" className="founder-testimonial-section">
        <div className="container">
          
          <div className="testimonial-card-block reveal-on-scroll">
            <h3 className="testimonial-section-title">What Clients Say</h3>

          

              <div className="client-profile-center">
                <div className="client-name">{currentTestimonial.name}</div>
                <div className="client-role">{currentTestimonial.role}</div>
              </div>
                <div className="testimonial-content-wrapper">
              <div className="quote-header-row">
                <div className="star-rating">
                  <Star size={16} fill="#2A7B74" stroke="none" />
                  <Star size={16} fill="#2A7B74" stroke="none" />
                  <Star size={16} fill="#2A7B74" stroke="none" />
                  <Star size={16} fill="#2A7B74" stroke="none" />
                  <Star size={16} fill="#2A7B74" stroke="none" />
                </div>
              </div>

              <p className="quote-text">"{currentTestimonial.quote}"</p>
            </div>

            <div className="testimonial-dots-bar">
              {testimonials.map((t, idx) => (
                <span
                  key={t.id}
                  className={`t-dot ${activeTestimonialIndex === idx ? 'active' : ''}`}
                  onClick={() => setActiveTestimonialIndex(idx)}
                />
              ))}
            </div>
          </div>

          <KickstartBanner onActionClick={onNavigateToContact} />

        </div>
      </section>
    </div>
  );
}

export default Home;
